import { supabase } from './supabaseClient';

/** PostgREST page size; avoids silent truncation (old .range(0, 9999) capped involvement). */
const TABLE_PAGE_SIZE = 1000;

/**
 * Load all rows from a Supabase table using stable keyset-style paging on `id`.
 *
 * @param {string} table — public table name
 * @returns {Promise<object[]>}
 */
async function fetchAllRows(table) {
  const out = [];
  let from = 0;
  while (true) {
    const to = from + TABLE_PAGE_SIZE - 1;
    const { data, error } = await supabase
      .from(table)
      .select('*')
      .order('id', { ascending: true })
      .range(from, to);
    if (error) {
      throw new Error(`fetchAllRows(${table}): ${error.message}`);
    }
    if (!data?.length) {
      break;
    }
    out.push(...data);
    if (data.length < TABLE_PAGE_SIZE) {
      break;
    }
    from += TABLE_PAGE_SIZE;
  }
  return out;
}

function countBy(arr, key) {
  const counts = {};
  arr.forEach(item => {
    const val = item[key] || '';
    if (val) counts[val] = (counts[val] || 0) + 1;
  });
  return counts;
}

function groupSum(arr, groupKey, sumKey) {
  const groups = {};
  arr.forEach(item => {
    const g = item[groupKey] || 'Unknown';
    if (!groups[g]) groups[g] = { count: 0, sum: 0 };
    groups[g].count += 1;
    groups[g].sum += (item[sumKey] || 0);
  });
  return groups;
}

/**
 * Total participants for one session row (Data Capture export).
 * Must match `load_session_rows` in `scripts/seed_final_tables.py` and
 * `scripts/check_involvement_totals.py`: prefer stored TotalParticipants, else male+female+disabled.
 */
export function sumSessionParticipants(s) {
  const tp = s.total_participants;
  if (tp != null && tp !== '') {
    const n = Number(tp);
    if (!Number.isNaN(n)) return n;
  }
  return (
    (Number(s.participants_male) || 0) +
    (Number(s.participants_female) || 0) +
    (Number(s.participants_disabled) || 0)
  );
}

/** Parse start_date to YYYY-MM for trend charts. */
function sessionMonthKey(s) {
  const d = s.start_date;
  if (d == null || d === '') return null;
  if (typeof d === 'number' && d > 30000) {
    const epoch = new Date(Date.UTC(1899, 11, 30) + d * 86400000);
    if (!Number.isNaN(epoch.getTime())) {
      return `${epoch.getUTCFullYear()}-${String(epoch.getUTCMonth() + 1).padStart(2, '0')}`;
    }
  }
  const str = String(d);
  const iso = str.match(/(\d{4})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}`;
  const dt = new Date(str);
  if (!Number.isNaN(dt.getTime())) {
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
  }
  return null;
}

function sumByField(arr, field) {
  const sums = {};
  arr.forEach(item => {
    const k = item[field] || '';
    if (!k) return;
    sums[k] = (sums[k] || 0) + sumSessionParticipants(item);
  });
  return sums;
}

export async function fetchDashboardData() {
  const [
    { data: regions },
    competitions,
    people,
    sessions,
    { data: summary },
    devOfficers,
    matchUmpires,
    grounds,
    clubs,
    schools,
    matches,
  ] = await Promise.all([
    supabase.from('kwata_final_regions').select('*'),
    fetchAllRows('kwata_final_competitions'),
    fetchAllRows('kwata_final_people'),
    fetchAllRows('kwata_final_sessions'),
    supabase.from('kwata_final_summary').select('*').limit(1),
    fetchAllRows('kwata_final_development_officers'),
    fetchAllRows('kwata_final_match_umpires'),
    fetchAllRows('kwata_final_grounds'),
    fetchAllRows('kwata_final_clubs'),
    fetchAllRows('kwata_final_schools'),
    fetchAllRows('kwata_final_matches'),
  ]);

  const sum = (summary && summary[0]) || {};

  // People analytics
  const people_by_role = countBy(people || [], 'role');
  const people_by_gender = countBy((people || []).filter(p => p.gender), 'gender');
  const qualification_levels = countBy((people || []).filter(p => p.qualification_level), 'qualification_level');
  const active_status = {};
  (people || []).forEach(p => {
    if (p.currently_active === 'Yes') active_status['Yes'] = (active_status['Yes'] || 0) + 1;
    else if (p.currently_active === 'No') active_status['No'] = (active_status['No'] || 0) + 1;
  });

  // Derive ball category and players-per-team from cricket_type (no DB migration needed)
  function getBallCategory(cricketType) {
    return cricketType === 'Modified' ? 'Modified Ball' : 'Hardball';
  }
  function getPlayersPerTeam(cricketType) {
    return cricketType === 'Modified' ? 8 : 15;
  }
  const enrichedCompetitions = (competitions || []).map(c => ({
    ...c,
    ball_category: getBallCategory(c.cricket_type),
    players_per_team: getPlayersPerTeam(c.cricket_type),
  }));

  // Competition analytics — combined
  const competition_by_type = {};
  enrichedCompetitions.forEach(c => {
    const key = `${c.cricket_type}_${c.gender}`;
    if (!competition_by_type[key]) {
      competition_by_type[key] = { count: 0, cricket_type: c.cricket_type, gender: c.gender };
    }
    competition_by_type[key].count += 1;
  });

  const compRegionCounts = countBy(enrichedCompetitions, 'region');
  const competition_by_region = Object.entries(compRegionCounts)
    .map(([region, count]) => ({ region, count }))
    .sort((a, b) => b.count - a.count);

  const ageCounts = countBy(enrichedCompetitions.filter(c => c.age_group), 'age_group');
  const age_groups = Object.entries(ageCounts)
    .map(([age_group, count]) => ({ age_group, count }))
    .sort((a, b) => b.count - a.count);

  const ball_types = countBy(enrichedCompetitions.filter(c => c.ball_type), 'ball_type');
  const scoring_methods = countBy(enrichedCompetitions.filter(c => c.scoring_method), 'scoring_method');
  const team_sizes = countBy(enrichedCompetitions.filter(c => c.team_size), 'team_size');

  // Overs distribution
  const oversCounts = countBy(enrichedCompetitions.filter(c => c.overs > 0), 'overs');
  const overs_distribution = Object.entries(oversCounts)
    .map(([overs, count]) => ({ overs: Number(overs), count }))
    .sort((a, b) => a.overs - b.overs);

  // Gender split
  const genderCounts = countBy(enrichedCompetitions, 'gender');
  const gender_split = {};
  Object.entries(genderCounts).forEach(([g, c]) => { gender_split[g] = { count: c }; });

  // Pathway
  const typeCounts = countBy(enrichedCompetitions, 'cricket_type');
  const pathway = Object.entries(typeCounts)
    .map(([type, competitions]) => ({ type, competitions }))
    .sort((a, b) => b.competitions - a.competitions);

  // Top competitions
  const compNameCounts = countBy(enrichedCompetitions.filter(c => c.competition_name), 'competition_name');
  const top_competitions = Object.entries(compNameCounts)
    .map(([name, teams]) => ({ name, teams }))
    .sort((a, b) => b.teams - a.teams)
    .slice(0, 20);

  // ── Per ball-category analytics (Hardball vs Modified Ball) ──────────────────
  // For Modified ball: U08–U13 = Junior, U14+ = Senior (age-group based)
  // For Hardball: cricket_type='Junior' = Junior, cricket_type='Senior' = Senior
  //   (U14–U19 in the Junior file ARE juniors — the file determines the tier, not the age group)
  const MODIFIED_JUNIOR_AGES = new Set(['U08', 'U09', 'U10', 'U11', 'U12', 'U13']);

  function buildCategoryStats(rows, ballCategory) {
    const byType = {};
    rows.forEach(c => {
      const key = `${c.cricket_type}_${c.gender}`;
      if (!byType[key]) byType[key] = { count: 0, cricket_type: c.cricket_type, gender: c.gender };
      byType[key].count += 1;
    });
    const byRegion = Object.entries(countBy(rows, 'region'))
      .map(([region, count]) => ({ region, count }))
      .sort((a, b) => b.count - a.count);
    const byGender = {};
    Object.entries(countBy(rows, 'gender')).forEach(([g, c]) => { byGender[g] = { count: c }; });
    const ageGroupCounts = countBy(rows.filter(c => c.age_group), 'age_group');
    const ageGroups = Object.entries(ageGroupCounts)
      .map(([age_group, count]) => ({ age_group, count }))
      .sort((a, b) => b.count - a.count);
    const totalTeams = rows.length;
    const playersPerTeam = rows.length > 0 ? rows[0].players_per_team : 0;
    const totalPlayers = totalTeams * playersPerTeam;
    const compNames = countBy(rows.filter(c => c.competition_name), 'competition_name');
    const topComps = Object.entries(compNames)
      .map(([name, teams]) => ({ name, teams }))
      .sort((a, b) => b.teams - a.teams)
      .slice(0, 15);
    const oversCountsCat = countBy(rows.filter(c => c.overs > 0), 'overs');
    const oversDistribution = Object.entries(oversCountsCat)
      .map(([overs, count]) => ({ overs: Number(overs), count }))
      .sort((a, b) => a.overs - b.overs);

    // Junior / Senior split — logic differs by ball category:
    // Hardball: use cricket_type field (Junior file = Junior, Senior file = Senior)
    // Modified: use age group cutoff (U08-U13 = Junior, U14+ = Senior)
    let juniorRows, seniorRows;
    if (ballCategory === 'Hardball') {
      juniorRows = rows.filter(r => r.cricket_type === 'Junior');
      seniorRows = rows.filter(r => r.cricket_type === 'Senior');
    } else {
      juniorRows = rows.filter(r => MODIFIED_JUNIOR_AGES.has(r.age_group));
      seniorRows = rows.filter(r => r.age_group && !MODIFIED_JUNIOR_AGES.has(r.age_group));
    }

    const juniorByGender = {};
    Object.entries(countBy(juniorRows, 'gender')).forEach(([g, c]) => { juniorByGender[g] = c; });
    const seniorByGender = {};
    Object.entries(countBy(seniorRows, 'gender')).forEach(([g, c]) => { seniorByGender[g] = c; });

    const juniorSeniorSplit = {
      junior: {
        teams: juniorRows.length,
        players: juniorRows.length * playersPerTeam,
        male: juniorByGender['Male'] || 0,
        female: juniorByGender['Female'] || 0,
      },
      senior: {
        teams: seniorRows.length,
        players: seniorRows.length * playersPerTeam,
        male: seniorByGender['Male'] || 0,
        female: seniorByGender['Female'] || 0,
      },
    };

    return {
      byType, byRegion, byGender, ageGroups, totalTeams, playersPerTeam, totalPlayers,
      topComps, oversDistribution, juniorSeniorSplit,
    };
  }

  const hardballRows = enrichedCompetitions.filter(c => c.ball_category === 'Hardball');
  const modifiedRows = enrichedCompetitions.filter(c => c.ball_category === 'Modified Ball');
  const competitions_hardball = buildCategoryStats(hardballRows, 'Hardball');
  // Modified Ball has no Junior/Senior tier — null out the split so the panel is hidden
  const _modifiedStats = buildCategoryStats(modifiedRows, 'Modified Ball');
  const competitions_modified = { ..._modifiedStats, juniorSeniorSplit: null };
  const competitions_combined_total_teams = enrichedCompetitions.length;
  const competitions_combined_total_players =
    hardballRows.length * 15 + modifiedRows.length * 8;

  // Sessions analytics
  const sessionRegionGroups = {};
  (sessions || []).forEach(s => {
    const r = s.region || 'Unknown';
    if (!sessionRegionGroups[r]) {
      sessionRegionGroups[r] = { count: 0, male: 0, female: 0, disabled: 0, total: 0 };
    }
    sessionRegionGroups[r].count += 1;
    sessionRegionGroups[r].male += Number(s.participants_male) || 0;
    sessionRegionGroups[r].female += Number(s.participants_female) || 0;
    sessionRegionGroups[r].disabled += Number(s.participants_disabled) || 0;
    sessionRegionGroups[r].total += sumSessionParticipants(s);
  });
  const sessions_by_region = Object.entries(sessionRegionGroups)
    .map(([region, v]) => ({ region, ...v }))
    .sort((a, b) => b.total - a.total);

  const activity_types = countBy((sessions || []).filter(s => s.activity_type), 'activity_type');
  const venue_types = countBy((sessions || []).filter(s => s.venue_type), 'venue_type');
  const org_types = countBy((sessions || []).filter(s => s.organisation_type), 'organisation_type');

  const frequency_types = countBy((sessions || []).filter(s => s.frequency_type), 'frequency_type');
  const delivery_types = countBy((sessions || []).filter(s => s.delivery_type), 'delivery_type');

  const activity_types_participants = sumByField(sessions || [], 'activity_type');
  const venue_types_participants = sumByField(sessions || [], 'venue_type');
  const org_types_participants = sumByField(sessions || [], 'organisation_type');
  const frequency_types_participants = sumByField(sessions || [], 'frequency_type');
  const delivery_types_participants = sumByField(sessions || [], 'delivery_type');

  // National involvement (full Data Capture export — all rows)
  let involvement_total_participants = 0;
  let involvement_unallocated_participants = 0;
  let involvement_total_num_sessions = 0;
  let involvement_disabled_total = 0;
  let involvement_rows_unmapped_region = 0;
  (sessions || []).forEach(s => {
    const p = sumSessionParticipants(s);
    involvement_total_participants += p;
    involvement_total_num_sessions += Number(s.num_sessions) || 0;
    involvement_disabled_total += Number(s.participants_disabled) || 0;
    if (!s.region || String(s.region).trim() === '') {
      involvement_unallocated_participants += p;
      involvement_rows_unmapped_region += 1;
    }
  });
  const involvement_mapped_participants = involvement_total_participants - involvement_unallocated_participants;

  // Monthly trend (by session report start_date)
  const monthTrend = {};
  (sessions || []).forEach(s => {
    const mk = sessionMonthKey(s);
    if (!mk) return;
    if (!monthTrend[mk]) monthTrend[mk] = { month: mk, reports: 0, participants: 0 };
    monthTrend[mk].reports += 1;
    monthTrend[mk].participants += sumSessionParticipants(s);
  });
  const sessions_trend_monthly = Object.values(monthTrend).sort((a, b) => a.month.localeCompare(b.month));

  // Age ranges
  const ageRangeGroups = {};
  (sessions || []).filter(s => s.age_range_start > 0).forEach(s => {
    const key = `${s.age_range_start}-${s.age_range_end}`;
    if (!ageRangeGroups[key]) {
      ageRangeGroups[key] = { start: s.age_range_start, end: s.age_range_end, count: 0, participants: 0 };
    }
    ageRangeGroups[key].count += 1;
    ageRangeGroups[key].participants += sumSessionParticipants(s);
  });
  const age_ranges = Object.values(ageRangeGroups).sort((a, b) => a.start - b.start);

  // Top organisations
  const orgGroups = {};
  (sessions || []).filter(s => s.organisation).forEach(s => {
    if (!orgGroups[s.organisation]) orgGroups[s.organisation] = { sessions: 0, participants: 0, region: s.region };
    orgGroups[s.organisation].sessions += 1;
    orgGroups[s.organisation].participants += sumSessionParticipants(s);
  });
  const top_organisations = Object.entries(orgGroups)
    .map(([name, v]) => ({ name, ...v }))
    .sort((a, b) => b.participants - a.participants)
    .slice(0, 20);

  // GPS points
  const gpsMap = {};
  (sessions || []).filter(s => s.latitude && s.longitude && s.latitude !== 0).forEach(s => {
    const key = `${Math.round(s.latitude * 100)}_${Math.round(s.longitude * 100)}`;
    if (!gpsMap[key]) gpsMap[key] = { lat: s.latitude, lng: s.longitude, count: 0, participants: 0, location: s.location, region: s.region };
    gpsMap[key].count += 1;
    gpsMap[key].participants += (s.total_participants || 0);
  });
  const gps_points = Object.values(gpsMap);

  // Schools by region
  const schoolRegionCounts = countBy(schools || [], 'region');
  const schools_by_region = Object.entries(schoolRegionCounts)
    .map(([region, count]) => ({ region, count }))
    .sort((a, b) => b.count - a.count);

  // Match summary
  const match_summary = {
    total_matches: (matches || []).length,
    total_grounds: (grounds || []).length,
    total_clubs: (clubs || []).length,
    total_umpires: (matchUmpires || []).length,
  };

  // Series data
  const seriesGroups = {};
  (matches || []).forEach(m => {
    const key = `${m.series}|${m.series_sheet}`;
    if (!seriesGroups[key]) seriesGroups[key] = { series: m.series, sheet: m.series_sheet, matches: 0 };
    seriesGroups[key].matches += 1;
  });
  const series_data = Object.values(seriesGroups).sort((a, b) => b.matches - a.matches);

  const development_officers = [...(devOfficers || [])].sort(
    (a, b) => (Number(b.total_participants) || 0) - (Number(a.total_participants) || 0)
  );
  const matchUmpiresSorted = [...(matchUmpires || [])].sort(
    (a, b) => (Number(b.match_count) || 0) - (Number(a.match_count) || 0)
  );
  const groundsSorted = [...(grounds || [])].sort(
    (a, b) => (Number(b.match_count) || 0) - (Number(a.match_count) || 0)
  );
  const clubsSorted = [...(clubs || [])].sort(
    (a, b) => (Number(b.appearances) || 0) - (Number(a.appearances) || 0)
  );

  return {
    regions: regions || [],
    summary: sum,
    people_by_role,
    people_by_gender,
    qualification_levels,
    active_status,
    competition_by_type,
    competition_by_region,
    age_groups,
    ball_types,
    overs_distribution,
    scoring_methods,
    team_sizes,
    gender_split,
    pathway,
    unique_teams: sum.unique_teams || 0,
    unique_competitions: sum.unique_competitions || 0,
    top_competitions,
    sessions_by_region,
    activity_types,
    venue_types,
    org_types,
    frequency_types,
    delivery_types,
    activity_types_participants,
    venue_types_participants,
    org_types_participants,
    frequency_types_participants,
    delivery_types_participants,
    involvement_total_participants,
    involvement_unallocated_participants,
    involvement_mapped_participants,
    involvement_total_num_sessions,
    involvement_disabled_total,
    involvement_rows_unmapped_region,
    involvement_session_report_count: (sessions || []).length,
    sessions_trend_monthly,
    age_ranges,
    top_organisations,
    development_officers,
    gps_points,
    schools_by_region,
    match_summary,
    series_data,
    competitions_hardball,
    competitions_modified,
    competitions_combined_total_teams,
    competitions_combined_total_players,
    // Raw data for sub-pages
    _people: people || [],
    _competitions: enrichedCompetitions,
    _matches: matches || [],
    _grounds: groundsSorted,
    _clubs: clubsSorted,
    _matchUmpires: matchUmpiresSorted,
    _schools: schools || [],
    _sessions: sessions || [],
  };
}

const PATHWAY_KEYS = [
  'modified_male',
  'modified_female',
  'junior_male',
  'junior_female',
  'senior_male',
  'senior_female',
];

/**
 * Per-region detail for the map panel. Involvement metrics come only from
 * `kwata_final_sessions` (+ session-derived schools list). Competition metrics
 * come from competition templates, people register, etc. The two are not mixed;
 * they are only aligned by region name.
 *
 * @param {string} regionName
 * @param {object} allData — payload from fetchDashboardData()
 * @returns {Promise<object|null>}
 */
export async function fetchRegionDetail(regionName, allData) {
  const region = allData.regions.find(r => r.name === regionName);
  if (!region) return null;

  const sessions = (allData._sessions || []).filter(s => s.region === regionName);
  let participantsTotal = 0;
  let male = 0;
  let female = 0;
  let disabled = 0;
  let numSessionsDelivery = 0;
  sessions.forEach(s => {
    participantsTotal += sumSessionParticipants(s);
    male += Number(s.participants_male) || 0;
    female += Number(s.participants_female) || 0;
    disabled += Number(s.participants_disabled) || 0;
    numSessionsDelivery += Number(s.num_sessions) || 0;
  });

  const session_organisations = countBy(sessions.filter(s => s.organisation), 'organisation');
  const activity_types = countBy(sessions.filter(s => s.activity_type), 'activity_type');
  const deliverers = countBy(sessions.filter(s => s.deliverer_name), 'deliverer_name');

  const sortedOrgs = Object.entries(session_organisations)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10);
  const sortedDeliverers = Object.entries(deliverers)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  const schools = (allData._schools || []).filter(s => s.region === regionName);

  const comps = (allData._competitions || []).filter(c => c.region === regionName);
  const comp_breakdown = {};
  const age_breakdown = {};
  const ball_breakdown = {};
  comps.forEach(c => {
    const key = `${c.cricket_type}_${c.gender}`;
    if (!comp_breakdown[key]) {
      comp_breakdown[key] = { type: c.cricket_type, gender: c.gender, count: 0 };
    }
    comp_breakdown[key].count += 1;
    if (c.age_group) age_breakdown[c.age_group] = (age_breakdown[c.age_group] || 0) + 1;
    if (c.ball_type) ball_breakdown[c.ball_type] = (ball_breakdown[c.ball_type] || 0) + 1;
  });

  const people = (allData._people || []).filter(p => p.region === regionName);
  const people_roles = countBy(people, 'role');

  const unique_teams = new Set(comps.filter(c => c.team_name).map(c => c.team_name)).size;
  const unique_competitions = new Set(comps.filter(c => c.competition_name).map(c => c.competition_name)).size;

  const pathwayCounts = {};
  PATHWAY_KEYS.forEach((k) => {
    pathwayCounts[k] = Number(region[k]) || 0;
  });

  return {
    regionMeta: {
      name: region.name,
      capital: region.capital,
      cities: region.cities,
    },
    involvement: {
      participantsTotal,
      male,
      female,
      disabled,
      sessionReports: sessions.length,
      numSessionsDelivery,
      session_organisations: Object.fromEntries(sortedOrgs),
      activity_types,
      deliverers: Object.fromEntries(sortedDeliverers),
      schools,
    },
    competitions: {
      competition_breakdown: Object.values(comp_breakdown),
      age_breakdown: Object.fromEntries(
        Object.entries(age_breakdown).sort(([, a], [, b]) => b - a)
      ),
      ball_breakdown,
      unique_teams,
      unique_competitions,
      total_entries: comps.length,
      people_roles,
      people,
      pathwayCounts,
      teachers: Number(region.teachers) || 0,
    },
  };
}
